import { useEffect } from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { Outlet, useParams } from 'react-router-dom';
import { RootState } from 'client/store/configureStore';
import { getAbilities } from 'client/api-utils';
import { WebsocketEvents } from 'common/enums/notification-service-enums';
import { useSocket } from 'client/store/notification-service/socket-context';
import { TopBar } from 'client/views/navigation/top-bar/top-bar';
import { ProjectSideBar } from 'client/views/navigation/project-side-bar/project-side-bar';
import { customerReset, getCustomers, customerSelected as setCustomerSelected } from 'client/store/entities/customer/customer';
import { getCustomerState } from 'client/store/entities/customer/selectors';
import { resetArchives } from 'client/store/entities/hierarchy/archive/archive';
import { getUserUnitsTemplatesData } from 'client/store/entities/unit-templates/unit-templates';
import 'client/views/request-interceptor';
import { Flex, Spinner } from '@oliasoft-open-source/react-ui-library';
import { featureFlagsInitialized } from 'client/store/entities/feature-flags/feature-flags';
import { getIPMFilesData } from 'client/store/entities/ipm-files/ipm-files';

const Main = ({
  customers, selectedCustomer, getCustomers, setCustomerSelected, customerReset, resetArchives,
}: PropsFromRedux) => {
  const { companyId } = useParams();
  const { socket, isConnected } = useSocket();
  const dispatch = useDispatch();

  useEffect(() => {
    if ((companyId && !selectedCustomer) || (companyId && selectedCustomer?.companyId !== companyId)) {
      const customer = customers.find((customer) => customer.companyId === companyId);
      setCustomerSelected(customer);

      const payload = {
        scope: 'GROUP',
        value: companyId,
      };

      if (isConnected) {
        socket.emit(WebsocketEvents.ChangeScope, payload);
      }
    }
  }, [companyId, customers, selectedCustomer, setCustomerSelected]);

  useEffect(() => {
    getCustomers();

    return () => {
      customerReset();
      resetArchives();
    };
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      getAbilities().then(() => {
        dispatch(featureFlagsInitialized());
      });
      dispatch(getUserUnitsTemplatesData());
      dispatch(getIPMFilesData());
    }
  }, [selectedCustomer]);

  if (!companyId || !selectedCustomer?.companyId) {
    return <Flex justifyContent="center"><Spinner dark tiny /></Flex>;
  }

  return (
    <>
      <TopBar />
      <ProjectSideBar />
      <Outlet />
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { customers, selectedCustomer } = getCustomerState(state);
  return { customers, selectedCustomer };
};

const mapDispatchToProps = {
  getCustomers,
  setCustomerSelected,
  customerReset,
  resetArchives,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const MainContainer = connector(Main);
